/**
 * Magic-link login — calm cinematic passwordless auth.
 *
 * UX intent: feel like a single calm investigative document, not a SaaS
 * login form. One field, one button, generous typography, electric-blue
 * accent rail. No "Sign up" / "Sign in" toggle — first login IS the signup.
 *
 * Pages:
 *   /login                       request a magic link
 *   /auth/check-your-email       confirmation card (after submit)
 *   /auth/verify                 lands here from email — backend handles the redirect
 *
 * Backend contract:
 *   POST /api/auth/magic-link/request {email, next}
 *     → 200 {ok, message}  (always — no enumeration)
 *   GET  /api/auth/magic-link/verify?token=...&next=...
 *     → 302 to {next} on success (sets access_token cookie)
 *     → 302 to /login?error=expired|invalid|used|missing on failure
 *
 * Direct-link return flow: any "Member sign in" link includes ?next=<current path>
 * so the user is dropped back exactly where they came from after auth.
 */

import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Mail, ArrowRight, ArrowLeft, Lock } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/analytics";

const ERROR_MESSAGES = {
    expired: "That link has expired. Request a fresh one below — it's instant.",
    invalid: "We couldn't verify that link. Request a fresh one below.",
    used: "That link has already been used. Request a fresh one below.",
    missing: "Something interrupted the sign-in. Try again below.",
};

function Frame({ children }) {
    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1] flex flex-col">
            {/* Top accent rail — matches OG card / membership email */}
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />
            <main className="flex-1 flex items-start justify-center px-6 pt-20 pb-16">
                <div className="w-full max-w-md">{children}</div>
            </main>
            <footer className="text-center pb-10 px-6 text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                <Link to="/" className="hover:text-[#56CCF2] transition-colors inline-flex items-center gap-1.5" data-testid="login-back-home">
                    <ArrowLeft className="w-3 h-3" />
                    Back to the library
                </Link>
            </footer>
        </div>
    );
}

function Eyebrow() {
    return (
        <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
                <div className="h-9 px-2 flex items-center justify-center rounded-md bg-white"><img src={require("../assets/logo-tcp-mark.png")} alt="The Context Project" className="h-6 w-auto select-none" draggable={false} /></div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-semibold">The Context Project</div>
                    <div className="text-[11px] text-[#64748B]">Research library · members archive</div>
                </div>
            </div>
        </div>
    );
}

export default function LoginPage() {
    const [params] = useSearchParams();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const error = params.get("error");
    const nextPath = params.get("next") || "/";

    useEffect(() => { track("tcp_login_view", { has_error: !!error }); }, [error]);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || busy) return;
        setBusy(true);
        try {
            await api.post("/auth/magic-link/request", { email: email.trim(), next: nextPath });
            track("tcp_login_request", { next: nextPath });
            setSubmitted(true);
        } catch (err) {
            // Backend always returns 200 — only surface a calm error if the
            // network itself failed. Even then we move forward (privacy: no enum).
            track("tcp_login_request_error", { next: nextPath });
            setSubmitted(true);
        } finally {
            setBusy(false);
        }
    };

    if (submitted) {
        return (
            <Frame>
                <Eyebrow />
                <div data-testid="login-sent" className="bg-[#111827] border border-[#243044] rounded-2xl p-7">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-semibold mb-3 inline-flex items-center gap-1.5">
                        <Mail className="w-3 h-3" />
                        Check your inbox
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight mb-3">
                        Your link is on its way.
                    </h1>
                    <p className="text-sm text-[#94A3B8] leading-relaxed mb-5">
                        We've sent a secure one-time link to{" "}
                        <span className="text-[#CBD5E1] font-medium">{email}</span>.
                        It works once, and stays valid for 30&nbsp;minutes.
                    </p>
                    <p className="text-xs text-[#64748B] leading-relaxed">
                        Not finding it? Check the promotions folder. If you used a different email at checkout,
                        request a fresh link with that one.
                    </p>
                    <button
                        type="button"
                        data-testid="login-try-different"
                        onClick={() => { setSubmitted(false); setEmail(""); }}
                        className="mt-6 text-xs uppercase tracking-[0.2em] text-[#64748B] hover:text-[#56CCF2] transition-colors"
                    >
                        Use a different email
                    </button>
                </div>
            </Frame>
        );
    }

    return (
        <Frame>
            <Eyebrow />
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight leading-tight mb-4" data-testid="login-headline">
                Continue your investigation
            </h1>
            <p className="text-sm sm:text-[15px] text-[#94A3B8] leading-relaxed mb-8 max-w-sm">
                Enter the email linked to your membership. We'll send a secure one-time link — no password, no friction.
            </p>

            {error && ERROR_MESSAGES[error] && (
                <div data-testid="login-error" className="mb-6 px-4 py-3 rounded-xl bg-[#111827] border border-[#243044]">
                    <p className="text-sm text-[#CBD5E1] leading-relaxed">{ERROR_MESSAGES[error]}</p>
                </div>
            )}

            <form onSubmit={submit} className="space-y-4">
                <div className="bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED] transition-colors">
                    <label htmlFor="email" className="block text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-1">
                        Membership email
                    </label>
                    <input
                        id="email"
                        type="email"
                        required
                        autoFocus
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        data-testid="login-email-input"
                        className="w-full bg-transparent border-0 outline-none text-base text-white placeholder-[#64748B]"
                    />
                </div>
                <button
                    type="submit"
                    disabled={busy || !email.trim()}
                    data-testid="login-submit"
                    className={`group w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold tracking-wide transition-colors ${busy || !email.trim() ? "bg-[#243044] text-[#64748B] cursor-not-allowed" : "bg-[#56CCF2] text-[#0B1220] hover:bg-white"}`}
                >
                    {busy ? "Sending…" : "Send my link"}
                    {!busy && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />}
                </button>
            </form>

            <div className="mt-10 pt-6 border-t border-[#243044]">
                <p className="text-xs text-[#64748B] leading-relaxed inline-flex items-start gap-2">
                    <Lock className="w-3 h-3 mt-0.5 shrink-0 text-[#56CCF2]/60" />
                    <span>
                        Not a member yet?{" "}
                        <a
                            href={process.env.REACT_APP_MEMBERSHIP_URL || "/membership"}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="login-become-member"
                            className="text-[#56CCF2] hover:text-white transition-colors"
                        >
                            Become a member →
                        </a>
                    </span>
                </p>
            </div>
        </Frame>
    );
}

/**
 * /auth/verify — the page the email link points to.
 *
 * We use a tiny client-side passthrough: when the page mounts, we
 * immediately call the backend verify endpoint via window.location so the
 * backend can set the cookie + redirect server-side. (Server-set cookies +
 * 302 are essential — fetch() in the SPA wouldn't be able to set HttpOnly
 * cookies cross-origin in some browser configs.)
 *
 * This component is therefore mostly a calm "Verifying…" placeholder for
 * the ~300ms before the redirect lands.
 */
export function MagicLinkVerifyPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const nextPath = params.get("next") || "/";

    useEffect(() => {
        track("tcp_login_verify_start", { next: nextPath });
        if (!token) {
            window.location.href = "/login?error=missing";
            return;
        }
        // Hand off to the backend (which does the actual verification +
        // sets the cookie + redirects). Same-origin in production —
        // REACT_APP_BACKEND_URL === REACT_APP_PUBLIC_SITE_URL.
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const url = `${base}/api/auth/magic-link/verify?token=${encodeURIComponent(token)}&next=${encodeURIComponent(nextPath)}`;
        window.location.href = url;
    }, [token, nextPath]);

    return (
        <Frame>
            <Eyebrow />
            <div className="bg-[#111827] border border-[#243044] rounded-2xl p-7" data-testid="verify-pending">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-semibold mb-3 inline-flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    Verifying
                </div>
                <h1 className="text-2xl font-bold text-white tracking-tight mb-2">
                    Opening your investigation…
                </h1>
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                    One moment — confirming your link and dropping you back where you left off.
                </p>
                <div className="mt-6 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#56CCF2] animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#56CCF2]/60 animate-pulse" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#56CCF2]/30 animate-pulse" style={{ animationDelay: "240ms" }} />
                </div>
            </div>
        </Frame>
    );
}
